import React, { useMemo } from 'react';
import classNames from 'classnames';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';
import { DATA_ROOM_LOGIN_SOURCE } from 'models/DataRoomLoginSource';
import { NEW_WINDOW_PROPS } from 'data/global';
import LinkToIcon from 'icons/LinkTo.icon';
import DataRoomNodePreview from './components/DataRoomNodePreview';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import { SIZE } from 'models/Size';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';

import './PortfolioDataRoomNodes.styles.scss';

const TOO_MANY_DOCUMENTS = 5;

type PortfolioDataRoomNodesProps = {
  className?: string;
  /**
   * `compact` titles the section the way the contact preview's other panels do — the same
   * heading and description as "Files in chat", which it sits under. `default` is the buyer
   * portfolio aside, where the section heads the column on its own.
   */
  variant?: 'default' | 'compact';
  dataRoomId: string;
  dataRoomNodes: DataRoomNode[];
  dataRoomName: string;
  ownerId: string;
  userSide: QAndASides;
  dealRoomId: string;
  /**
   * Splits "no access" from "access but nothing shared yet" — without it the node count is
   * the only signal and the two read the same. Omitted, the section behaves as it always has.
   */
  hasDataRoomAccess?: boolean;
};

const PortfolioDataRoomNodes: React.FC<PortfolioDataRoomNodesProps> = (
  props,
) => {
  const {
    className,
    variant = 'default',
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    dealRoomId,
    dataRoomNodes = [],
    hasDataRoomAccess,
  } = props;

  // callers that don't pass the prop keep the original count-only behaviour
  const isAccessKnown = hasDataRoomAccess !== undefined;
  const hasNoAccess = isAccessKnown && !hasDataRoomAccess;

  const isCompact = variant === 'compact';

  const classes = classNames(
    'portfolio-data-room-nodes',
    `portfolio-data-room-nodes--${variant}`,
    className,
  );

  const { t } = useTranslation();

  const numberOfDocuments = useMemo(
    () => dataRoomNodes.length,
    [dataRoomNodes],
  );

  const hasTooManyDocuments = useMemo(
    () => numberOfDocuments > TOO_MANY_DOCUMENTS,
    [numberOfDocuments],
  );

  const RenderMessage = useMemo(() => {
    if (hasNoAccess) return <p>{t('dataRoomNoAccessForDeal')}</p>;

    if (!numberOfDocuments)
      return (
        <p>
          {t(
            isAccessKnown
              ? 'noDocumentsSharedYet'
              : 'documentMessageForNoDocuments',
          )}
        </p>
      );

    return (
      <p>
        {t(
          hasTooManyDocuments
            ? 'documentMessageForTooManyDocuments'
            : 'documentsMessage',
          { count: numberOfDocuments },
        )}
      </p>
    );
  }, [numberOfDocuments, hasTooManyDocuments, hasNoAccess, isAccessKnown, t]);

  const RenderDocs = useMemo(() => {
    // nothing to open, and the "Data room" button below would link into a room they can't reach
    if (hasNoAccess) return null;

    if (!numberOfDocuments) return null;

    if (hasTooManyDocuments)
      return (
        <Link
          className="ncoded-button ncoded-button--solid"
          to={getDataRoomRoute(dataRoomId, undefined, DATA_ROOM_LOGIN_SOURCE.PORTFOLIO)}
          {...NEW_WINDOW_PROPS}
        >
          <LinkToIcon />
          {t('dataRoom')}
        </Link>
      );

    return (
      <div className="container">
        {dataRoomNodes?.map((node) => (
          <DataRoomNodePreview
            key={node._id}
            node={node}
            siblings={dataRoomNodes}
            dataRoomName={dataRoomName}
            ownerId={ownerId}
            userSide={userSide}
            dealRoomId={dealRoomId}
          />
        ))}
      </div>
    );
  }, [
    numberOfDocuments,
    hasTooManyDocuments,
    dataRoomId,
    dataRoomNodes,
    dataRoomName,
    ownerId,
    userSide,
    dealRoomId,
    hasNoAccess,
    t,
  ]);

  return (
    <div className={classes}>
      {/* the title and its message are one block, so the space between them can tighten
          without the documents below moving with it */}
      <div className="portfolio-data-room-nodes__intro">
        <div className="heading">
          <p>{t('dataRoom')}</p>
          <Counter
            count={numberOfDocuments}
            variant={COUNTER_VARIANTS.GREY}
            {...(isCompact && { size: SIZE.SMALL })}
          />
        </div>

        {RenderMessage}
      </div>

      {RenderDocs}
    </div>
  );
};

export default PortfolioDataRoomNodes;
